import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, UserPlus, Trash2 } from 'lucide-react';
import { api } from '../lib/api.ts';
import { extractError } from '../lib/types.ts';
import { PageHeader } from '../components/ui/PageHeader.tsx';
import { Button } from '../components/ui/Button.tsx';
import { Badge } from '../components/ui/Badge.tsx';
import { Modal, ModalFooter } from '../components/ui/Modal.tsx';
import { Input } from '../components/ui/Input.tsx';
import { SkeletonRows } from '../components/ui/Skeleton.tsx';
import { ConfirmDialog } from '../components/ui/ConfirmDialog.tsx';
import { useToast } from '../components/ui/Toast.tsx';
import { EventStatusBadge } from '../components/events/EventStatusBadge.tsx';
import {
  EventFormValues,
  EMPTY_EVENT_FORM,
  toLocalDatetimeValue,
  EventFormFields,
} from '../components/events/EventFormFields.tsx';

// ─── Types ────────────────────────────────────────────────────────────────────

interface EventItem {
  id: string;
  name: string;
  status: string;
  startsAt: string;
  endsAt: string | null;
  locationName: string | null;
  maxCapacity: number | null;
}

interface EventWithStats extends EventItem {
  confirmedCount: number;
  waitlistedCount: number;
  cancelledCount: number;
  description: string | null;
  locationAddress: string | null;
  registrationDeadline: string | null;
  imageUrl: string | null;
}

interface RsvpItem {
  id: string;
  customerId: string;
  status: string;
  registeredAt: string;
  cancelledAt: string | null;
  notes: string | null;
  customerFirstName: string | null;
  customerLastName: string | null;
  customerEmail: string | null;
}

function formatDate(dateStr: string) {
  return new Date(dateStr).toLocaleString('en-US', { dateStyle: 'short', timeStyle: 'short' });
}

function rsvpStatusVariant(status: string) {
  if (status === 'confirmed') return 'success' as const;
  if (status === 'waitlisted') return 'warning' as const;
  if (status === 'cancelled') return 'danger' as const;
  return 'default' as const;
}

function rsvpStatusLabel(status: string) {
  const map: Record<string, string> = {
    confirmed: 'Confirmed',
    waitlisted: 'Waitlisted',
    cancelled: 'Cancelled',
  };
  return map[status] ?? status;
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export function EventDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const toast = useToast();

  const [tab, setTab] = useState<'registrants' | 'settings'>('registrants');
  const [showAddRsvp, setShowAddRsvp] = useState(false);
  const [rsvpCustomerId, setRsvpCustomerId] = useState('');
  const [rsvpNotes, setRsvpNotes] = useState('');
  const [deleteRsvpTarget, setDeleteRsvpTarget] = useState<RsvpItem | null>(null);
  const [editForm, setEditForm] = useState<EventFormValues>(EMPTY_EVENT_FORM);

  const { data: event, isLoading: loadingEvent } = useQuery<EventWithStats>({
    queryKey: ['event-detail', id],
    queryFn: () =>
      api.get(`/api/v1/events/${id}`).then((r) => r.data),
    enabled: !!id,
  });

  const { data: rsvps = [], isLoading: loadingRsvps } = useQuery<RsvpItem[]>({
    queryKey: ['event-rsvps', id],
    queryFn: () =>
      api.get(`/api/v1/events/${id}/rsvps`).then((r) => r.data),
    enabled: !!id,
  });

  const addRsvpMutation = useMutation({
    mutationFn: (body: { customerId: string; notes?: string }) =>
      api.post(`/api/v1/events/${id}/rsvps`, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['event-rsvps', id] });
      qc.invalidateQueries({ queryKey: ['event-detail', id] });
      setShowAddRsvp(false);
      setRsvpCustomerId('');
      setRsvpNotes('');
      toast.success('Registrant added');
    },
    onError: (err) => toast.error(extractError(err, 'Error adding registration')),
  });

  const deleteRsvpMutation = useMutation({
    mutationFn: (rsvpId: string) =>
      api.delete(`/api/v1/events/${id}/rsvps/${rsvpId}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['event-rsvps', id] });
      qc.invalidateQueries({ queryKey: ['event-detail', id] });
      setDeleteRsvpTarget(null);
      toast.success('RSVP removed');
    },
    onError: (err) => toast.error(extractError(err, 'Error removing RSVP')),
  });

  useEffect(() => {
    if (event) {
      setEditForm({
        name: event.name ?? '',
        description: event.description ?? '',
        startsAt: toLocalDatetimeValue(event.startsAt),
        endsAt: toLocalDatetimeValue(event.endsAt),
        locationName: event.locationName ?? '',
        locationAddress: event.locationAddress ?? '',
        maxCapacity: event.maxCapacity != null ? String(event.maxCapacity) : '',
        registrationDeadline: toLocalDatetimeValue(event.registrationDeadline),
        imageUrl: event.imageUrl ?? '',
      });
    }
  }, [event]);

  const saveMutation = useMutation({
    mutationFn: (body: object) =>
      api.put(`/api/v1/events/${id}`, body).then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['event-detail', id] });
      qc.invalidateQueries({ queryKey: ['events'] });
      toast.success('Event updated');
    },
    onError: (err) => toast.error(extractError(err, 'Error saving event')),
  });

  function handleEditSubmit() {
    if (!editForm.name || !editForm.startsAt) return;
    saveMutation.mutate({
      name: editForm.name,
      description: editForm.description || undefined,
      startsAt: editForm.startsAt,
      endsAt: editForm.endsAt || undefined,
      locationName: editForm.locationName || undefined,
      locationAddress: editForm.locationAddress || undefined,
      maxCapacity: editForm.maxCapacity ? parseInt(editForm.maxCapacity, 10) : undefined,
      registrationDeadline: editForm.registrationDeadline || undefined,
      imageUrl: editForm.imageUrl || undefined,
    });
  }

  function setEdit(key: keyof EventFormValues, value: string) {
    setEditForm((f) => ({ ...f, [key]: value }));
  }

  if (loadingEvent) {
    return (
      <div className="bg-white border border-zinc-200 rounded-xl overflow-hidden">
        <SkeletonRows count={5} cols={4} />
      </div>
    );
  }

  if (!event) {
    return <p className="text-sm text-zinc-500">Event not found.</p>;
  }

  const remaining =
    event.maxCapacity != null ? event.maxCapacity - event.confirmedCount : null;

  return (
    <div>
      <PageHeader
        title={event.name}
        action={
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="sm" onClick={() => navigate('/events')}>
              <ArrowLeft className="w-3.5 h-3.5 mr-1.5" /> Back to list
            </Button>
          </div>
        }
      />

      {/* Header info */}
      <div className="flex items-center gap-3 mb-6">
        <EventStatusBadge status={event.status} />
        <span className="text-sm text-zinc-500">
          {formatDate(event.startsAt)}
          {event.endsAt && ` → ${formatDate(event.endsAt)}`}
        </span>
        {event.locationName && (
          <span className="text-sm text-zinc-400">· {event.locationName}</span>
        )}
      </div>

      {/* Stats row */}
      <div className="grid grid-cols-4 gap-4 mb-6">
        <div className="bg-white border border-zinc-200 rounded-lg p-4">
          <p className="text-xs text-zinc-400 uppercase tracking-wider font-medium">Confirmed</p>
          <p className="mt-1 text-2xl font-semibold text-zinc-900">{event.confirmedCount}</p>
        </div>
        <div className="bg-white border border-zinc-200 rounded-lg p-4">
          <p className="text-xs text-zinc-400 uppercase tracking-wider font-medium">Waitlisted</p>
          <p className="mt-1 text-2xl font-semibold text-zinc-900">{event.waitlistedCount}</p>
        </div>
        <div className="bg-white border border-zinc-200 rounded-lg p-4">
          <p className="text-xs text-zinc-400 uppercase tracking-wider font-medium">Cancelled</p>
          <p className="mt-1 text-2xl font-semibold text-zinc-900">{event.cancelledCount}</p>
        </div>
        <div className="bg-white border border-zinc-200 rounded-lg p-4">
          <p className="text-xs text-zinc-400 uppercase tracking-wider font-medium">Remaining spots</p>
          <p className="mt-1 text-2xl font-semibold text-zinc-900">
            {remaining != null ? remaining : '∞'}
          </p>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 border-b border-zinc-200 mb-6">
        {(['registrants', 'settings'] as const).map((t) => (
          <button
            key={t}
            onClick={() => setTab(t)}
            className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
              tab === t
                ? 'border-zinc-900 text-zinc-900'
                : 'border-transparent text-zinc-500 hover:text-zinc-700'
            }`}
          >
            {t === 'registrants' ? 'Registrants' : 'Settings'}
          </button>
        ))}
      </div>

      {/* Tab content */}
      {tab === 'registrants' && (
        <div>
          <div className="flex justify-end mb-4">
            <Button size="sm" onClick={() => setShowAddRsvp(true)}>
              <UserPlus className="w-3.5 h-3.5 mr-1.5" /> Add registrant
            </Button>
          </div>

          {loadingRsvps ? (
            <div className="bg-white border border-zinc-200 rounded-xl overflow-hidden">
              <SkeletonRows count={3} cols={5} />
            </div>
          ) : rsvps.length === 0 ? (
            <p className="text-sm text-zinc-400 text-center py-8">No registrants.</p>
          ) : (
            <div className="bg-white border border-zinc-200 rounded-xl overflow-hidden">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-zinc-100">
                    <th className="px-4 py-3 text-left text-xs font-medium text-zinc-400 uppercase tracking-wider">
                      Customer
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-zinc-400 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-zinc-400 uppercase tracking-wider">
                      Registered
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-zinc-400 uppercase tracking-wider">
                      Notes
                    </th>
                    <th className="px-4 py-3 w-16" />
                  </tr>
                </thead>
                <tbody className="divide-y divide-zinc-100">
                  {rsvps.map((rsvp) => {
                    const displayName =
                      [rsvp.customerFirstName, rsvp.customerLastName].filter(Boolean).join(' ') ||
                      rsvp.customerEmail ||
                      rsvp.customerId;
                    return (
                      <tr key={rsvp.id} className="hover:bg-zinc-50 group">
                        <td className="px-4 py-3">
                          <div className="font-medium text-zinc-900">{displayName}</div>
                          {rsvp.customerEmail && displayName !== rsvp.customerEmail && (
                            <div className="text-xs text-zinc-400">{rsvp.customerEmail}</div>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          <Badge variant={rsvpStatusVariant(rsvp.status)}>
                            {rsvpStatusLabel(rsvp.status)}
                          </Badge>
                        </td>
                        <td className="px-4 py-3 text-zinc-500 text-xs">
                          {formatDate(rsvp.registeredAt)}
                        </td>
                        <td className="px-4 py-3 text-zinc-500 text-xs">{rsvp.notes ?? '—'}</td>
                        <td className="px-4 py-3">
                          <div className="flex justify-end opacity-0 group-hover:opacity-100 transition-opacity">
                            <button
                              onClick={() => setDeleteRsvpTarget(rsvp)}
                              className="p-1.5 text-zinc-400 hover:text-red-600 hover:bg-red-50 rounded-md"
                              title="Remove RSVP"
                            >
                              <Trash2 size={13} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {tab === 'settings' && (
        <div className="max-w-2xl bg-white border border-zinc-200 rounded-xl p-6 space-y-4">
          <EventFormFields values={editForm} onChange={(k, v) => setEdit(k, v)} />

          {saveMutation.isError && (
            <p className="text-xs text-red-500">{extractError(saveMutation.error)}</p>
          )}

          <div className="flex justify-end pt-2">
            <Button
              loading={saveMutation.isPending}
              disabled={!editForm.name || !editForm.startsAt}
              onClick={handleEditSubmit}
            >
              Save changes
            </Button>
          </div>
        </div>
      )}

      {/* Add RSVP modal */}
      <Modal open={showAddRsvp} onClose={() => setShowAddRsvp(false)} title="Add registrant">
        <div className="space-y-4">
          <Input
            label="Customer ID (UUID) *"
            value={rsvpCustomerId}
            onChange={(e) => setRsvpCustomerId(e.target.value)}
            placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
          />
          <Input
            label="Notes"
            value={rsvpNotes}
            onChange={(e) => setRsvpNotes(e.target.value)}
            placeholder="Optional"
          />
          {addRsvpMutation.isError && (
            <p className="text-xs text-red-500">{extractError(addRsvpMutation.error)}</p>
          )}
        </div>
        <ModalFooter>
          <Button variant="ghost" size="sm" onClick={() => setShowAddRsvp(false)}>
            Cancel
          </Button>
          <Button
            loading={addRsvpMutation.isPending}
            disabled={!rsvpCustomerId.trim()}
            onClick={() =>
              addRsvpMutation.mutate({
                customerId: rsvpCustomerId.trim(),
                notes: rsvpNotes || undefined,
              })
            }
          >
            Add
          </Button>
        </ModalFooter>
      </Modal>

      {/* Delete RSVP confirm */}
      <ConfirmDialog
        open={!!deleteRsvpTarget}
        title="Remove registration?"
        message="The registration will be removed from the event."
        confirmLabel="Remove"
        variant="danger"
        loading={deleteRsvpMutation.isPending}
        onConfirm={() => deleteRsvpTarget && deleteRsvpMutation.mutate(deleteRsvpTarget.id)}
        onCancel={() => setDeleteRsvpTarget(null)}
      />
    </div>
  );
}
